const stripHtml = (html) => String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

export class ClipboardService {

  constructor(document) {
    this.document = document;
  }

  /**
   * Copy with both `text/html` and `text/plain` flavors so rich targets (Word, Excel,
   * Google Docs) keep formatting — tables keep their columns, headings/lists keep
   * structure — while plain targets still get readable text. Falls back to plain copy
   * when the async clipboard API is unavailable.
   */
  copyRich = async (html, plain) => {
    const plainText = plain != null ? plain : stripHtml(html);

    if (navigator.clipboard && typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
      try {
        const item = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
        return true;
      } catch (err) {
        console.warn('Clipboard rich copy failed, falling back to plain text:', err);
      }
    }

    return this.copyText(plainText);
  }

  copyText = async (textOrPromise) => {
    // Try modern Clipboard API first
    if (navigator.clipboard) {
      // If it's a promise and the browser supports ClipboardItem (required for mobile async copy)
      // This allows the browser to maintain the user gesture across the async boundary
      if (textOrPromise && typeof textOrPromise.then === 'function' && typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
        try {
          const item = new ClipboardItem({
            'text/plain': textOrPromise.then(text => new Blob([text], { type: 'text/plain' }))
          });
          await navigator.clipboard.write([item]);
          console.log('Clipboard: Text copied using modern Async API');
          return true;
        } catch (err) {
          console.warn('Clipboard Async API failed, falling back to sequential await:', err);
        }
      }

      // Standard copy process for strings or fallback for failed ClipboardItem
      if (navigator.clipboard.writeText) {
        try {
          const text = textOrPromise && typeof textOrPromise.then === 'function' ? await textOrPromise : textOrPromise;
          await navigator.clipboard.writeText(text);
          console.log('Clipboard: Text copied using modern writeText API');
          return true;
        } catch (err) {
          console.warn('Clipboard writeText API failed, trying legacy fallback:', err);
        }
      }
    }

    // Fallback to legacy execCommand method
    try {
      var copyText = this.document.createElement('textarea');
      copyText.value = textOrPromise;
      copyText.style.top = "0";
      copyText.style.left = "0";
      copyText.style.position = 'fixed';
      copyText.style.opacity = 0;
      this.document.body.appendChild(copyText);
      copyText.focus();
      copyText.select();
      copyText.setSelectionRange(0, 99999);
      const success = this.document.execCommand("copy");
      this.document.body.removeChild(copyText);

      if (success) {
        console.log('Clipboard: Text copied using legacy method');
        return true;
      } else {
        console.error('Clipboard: Legacy copy failed');
        return false;
      }
    } catch (err) {
      console.error('Clipboard: All copy methods failed:', err);
      return false;
    }
  }
}

export default ClipboardService;
